import React, { useState, useEffect } from 'react';
import {
  Table,
  Select,
  Popover,
  Checkbox,
  InputNumber,
  Button,
  Modal,
  message,
} from 'antd';
import Axios from 'axios';
import moment from 'moment';
import {
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
  dateSorter,
} from '../../common';
const { surveyCode } = require('../../Config/surveyCode');

const { Option } = Select;

// サーベイステータスのラベルと色（tone）の対応
// tone は StatusBadge の意味カラー（success/warning/error/processing/neutral）
const statusConfig = {
  0: { label: '実施前', tone: 'neutral' },
  1: { label: '一時保存中', tone: 'processing' },
  5: { label: '実施済み', tone: 'success' },
  7: { label: '期限切れ', tone: 'warning' },
  9: { label: '実施無効', tone: 'error' },
};

// 「登録から N 日以上経過」の既定値（サーバーの既定値と揃える）
const DEFAULT_UNUSED_DAYS = 30;

function TrialUserMgrPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignKey, setSelectedCampaignKey] = useState('');
  // 整理（削除）対象の絞り込み条件
  const [onlyCleanupTarget, setOnlyCleanupTarget] = useState(false);
  const [unusedDays, setUnusedDays] = useState(DEFAULT_UNUSED_DAYS);
  // 一括削除の選択状態
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // キャンペーン選択時にユーザーを取得
  useEffect(() => {
    if (selectedCampaignKey) {
      fetchTrialUsers(selectedCampaignKey, unusedDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignKey]);

  const fetchCampaigns = async () => {
    try {
      const res = await Axios.get('/api/trialCampaign/');
      if (res.data.success) {
        setCampaigns(res.data.campaigns);
        // 最初のキャンペーンを自動選択
        if (res.data.campaigns.length > 0) {
          setSelectedCampaignKey(res.data.campaigns[0].campaign_key);
        }
      }
    } catch (error) {
      console.error('キャンペーン一覧取得エラー:', error);
    }
  };

  const fetchTrialUsers = async (campaignKey, days) => {
    setLoading(true);
    try {
      const res = await Axios.get(
        `/api/trial/users?campaignKey=${encodeURIComponent(
          campaignKey
        )}&unusedDays=${encodeURIComponent(days || DEFAULT_UNUSED_DAYS)}`
      );
      if (res.data.success) {
        setUsers(res.data.users);
      }
    } catch (error) {
      console.error('トライアルユーザー取得エラー:', error);
    } finally {
      setLoading(false);
      // 一覧を取り直したら選択状態はクリアする（削除済みの行が残らないように）
      setSelectedRowKeys([]);
    }
  };

  const handleCampaignChange = (value) => {
    setSelectedCampaignKey(value);
    setSelectedRowKeys([]);
  };

  // 経過日数の変更は入力確定時（フォーカスアウト・Enter）にのみ再取得する
  const reloadWithUnusedDays = () => {
    if (!selectedCampaignKey) return;
    fetchTrialUsers(selectedCampaignKey, unusedDays);
  };

  // 整理対象（削除可能）の件数
  const cleanupTargetCount = users.filter(
    (user) => user.cleanupEligible === true
  ).length;

  // 表示するデータ（絞り込みが有効なときは整理対象のみ）
  const displayUsers = onlyCleanupTarget
    ? users.filter((user) => user.cleanupEligible === true)
    : users;

  // 選択中のユーザー情報（確認モーダルのメール一覧に使用）
  const selectedUsers = users.filter((user) =>
    selectedRowKeys.includes(user._id)
  );

  // 削除できなかったアカウントの一覧を表示する
  const showSkippedModal = (skipped) => {
    Modal.warning({
      title: '削除できなかったアカウント',
      width: 600,
      content: (
        <div
          style={{
            maxHeight: '30vh',
            overflowY: 'auto',
            marginTop: 'var(--space-3)',
          }}
        >
          {skipped.map((item) => (
            <div key={item._id} style={{ marginBottom: 'var(--space-2)' }}>
              <div>{item.email || '(メールアドレス不明)'}</div>
              <div style={{ color: 'var(--grey-65)' }}>{item.reason}</div>
            </div>
          ))}
        </div>
      ),
    });
  };

  // 一括削除の実行
  const executeDelete = async () => {
    setDeleting(true);
    try {
      const res = await Axios.delete('/api/trial/users', {
        data: {
          userIds: selectedRowKeys,
          unusedDays: unusedDays,
        },
        headers: { 'x-user-token': localStorage.getItem('w_auth') },
      });

      if (res.data.success) {
        message.success(
          `${res.data.deletedCount}件のアカウントを削除しました。`
        );
        setSelectedRowKeys([]);
        await fetchTrialUsers(selectedCampaignKey, unusedDays);
        if (res.data.skipped && res.data.skipped.length > 0) {
          showSkippedModal(res.data.skipped);
        }
      } else {
        message.error(
          res.data.message || 'アカウントの削除中にエラーが発生しました。'
        );
      }
    } catch (error) {
      console.error('トライアルアカウント削除エラー:', error);
      const serverMessage =
        error.response && error.response.data && error.response.data.message;
      message.error(
        serverMessage || 'アカウントの削除中にエラーが発生しました。'
      );
    } finally {
      setDeleting(false);
    }
  };

  // 削除前の確認モーダル
  const handleDeleteClick = () => {
    const campaign = campaigns.find(
      (c) => c.campaign_key === selectedCampaignKey
    );
    Modal.confirm({
      title: '選択したアカウントを削除します',
      width: 600,
      okText: '削除する',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      content: (
        <div>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            キャンペーン: {campaign ? campaign.campaign_name : '(不明)'}
          </div>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            削除件数: {selectedRowKeys.length}件
          </div>
          <div
            style={{
              maxHeight: '30vh',
              overflowY: 'auto',
              padding: 'var(--space-2)',
              border: '1px solid var(--grey-30)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {selectedUsers.map((user) => (
              <div key={user._id}>{user.email}</div>
            ))}
          </div>
          <div style={{ color: 'var(--red-active)' }}>
            削除したアカウントは復元できません。
          </div>
        </div>
      ),
      onOk: () => executeDelete(),
    });
  };

  // テーブルカラム定義
  // 長文列は ellipsisColumn で「… 省略 + ホバーで全文ツールチップ」化、
  // 各列に sorter（ヘッダークリックでソート）を付与。幅のドラッグ調整・
  // 横スクロールは useResizableColumns が配線する。
  const surveyCount = (record) =>
    record.surveyStatus ? Object.keys(record.surveyStatus).length : 0;

  const rawColumns = [
    {
      title: '状態',
      key: 'accountState',
      width: 140,
      sorter: (a, b) =>
        (a.trial_quick_login === true ? 1 : 0) -
        (b.trial_quick_login === true ? 1 : 0),
      render: (_, record) =>
        record.trial_quick_login === true ? (
          <StatusBadge tone="warning">簡易（メール未確認）</StatusBadge>
        ) : (
          <StatusBadge tone="success">正規</StatusBadge>
        ),
    },
    ellipsisColumn({
      title: '氏名',
      key: 'fullname',
      width: 120,
      sorter: (a, b) =>
        `${a.lastname || ''} ${a.name || ''}`.localeCompare(
          `${b.lastname || ''} ${b.name || ''}`,
          'ja'
        ),
      render: (_, record) => `${record.lastname || ''} ${record.name || ''}`,
    }),
    ellipsisColumn({
      title: 'メール',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: textSorter('email'),
    }),
    ellipsisColumn({
      title: '会社',
      dataIndex: 'company',
      key: 'company',
      width: 150,
      sorter: textSorter('company'),
    }),
    ellipsisColumn({
      title: '部署',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      sorter: textSorter('department'),
    }),
    {
      title: '登録日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      sorter: dateSorter('createdAt'),
      render: (date) => (date ? moment(date).format('YYYY/MM/DD') : ''),
    },
    {
      title: 'サーベイ',
      key: 'surveyCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => surveyCount(a) - surveyCount(b),
      render: (_, record) => {
        const codes = record.surveyStatus ? Object.keys(record.surveyStatus) : [];
        if (codes.length === 0) return '-';
        const content = (
          <div>
            {codes.map((code) => {
              const st = record.surveyStatus[code];
              const statusCfg = st
                ? statusConfig[st.survey_status] || { label: '不明', tone: 'neutral' }
                : { label: '未配布', tone: 'neutral' };
              return (
                <div key={code} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '2px 0' }}>
                  <span>{surveyCode[code] || code}</span>
                  <StatusBadge tone={statusCfg.tone} style={{ margin: 0 }}>{statusCfg.label}</StatusBadge>
                </div>
              );
            })}
          </div>
        );
        return (
          <Popover content={content} title="サーベイ状況">
            <span style={{ cursor: 'pointer', color: 'var(--blue-active)', textDecoration: 'underline' }}>
              {codes.length}件
            </span>
          </Popover>
        );
      },
    },
  ];

  // 列幅ドラッグ調整 + 初期フィット + 横スクロールの配線
  // rowSelection（チェックボックス列）があるため reservedWidth で幅を予約。
  const { columns, components, scroll, wrapRef } = useResizableColumns(
    rawColumns,
    { reservedWidth: 32 }
  );

  // 整理対象（削除可能）の行だけ選択できるようにする（誤操作の防止）
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.cleanupEligible !== true,
    }),
  };

  return (
    <div style={{ padding: '30px' }}>
      <h2>トライアルユーザー管理</h2>

      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span>キャンペーン:</span>
        <Select
          style={{ width: 300 }}
          value={selectedCampaignKey}
          onChange={handleCampaignChange}
          placeholder="キャンペーンを選択"
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {campaigns.map((c) => (
            <Option key={c.campaign_key} value={c.campaign_key}>
              {c.campaign_name}
            </Option>
          ))}
        </Select>
        <span style={{ color: 'var(--grey-65)' }}>
          登録ユーザー数: {users.length}名
        </span>
        <span style={{ color: 'var(--grey-65)' }}>
          うち 未着手の簡易: {cleanupTargetCount}名
        </span>
      </div>

      <div
        style={{
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <Checkbox
          checked={onlyCleanupTarget}
          onChange={(e) => {
            setOnlyCleanupTarget(e.target.checked);
            setSelectedRowKeys([]);
          }}
        >
          未着手の簡易アカウントのみ表示
        </Checkbox>
        <span>登録から</span>
        <InputNumber
          min={1}
          max={365}
          value={unusedDays}
          onChange={(value) => setUnusedDays(value)}
          onBlur={reloadWithUnusedDays}
          onPressEnter={reloadWithUnusedDays}
        />
        <span>日以上経過</span>
        <Button
          danger
          disabled={
            !selectedCampaignKey || selectedRowKeys.length === 0 || deleting
          }
          loading={deleting}
          onClick={handleDeleteClick}
        >
          選択したアカウントを削除
        </Button>
      </div>

      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          dataSource={displayUsers}
          rowKey="_id"
          rowSelection={rowSelection}
          loading={loading}
          scroll={scroll}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `全 ${total} 件`,
          }}
        />
      </div>
    </div>
  );
}

export default TrialUserMgrPage;
